import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';

import Dropdown from '@common/components/dropdown';
import { PredicateSelectorDate } from '@common/components/predicates-filter/filter-date';
import { PredicateSelectorMultiSelect } from '@common/components/predicates-filter/filter-multi-select';

import { EPredicateOperators } from '@common/definitions';
import { ScreenQuestionItem } from '@modules/learning/types/objects';
import { AnswerPredicate, Predicate } from '@common/types/objects';
import { PredicateSelectorNumber } from '@common/components/predicates-filter/filter-number';
import SearchBar from '@common/components/search-bar';

const DEFAULT_PREDICATE: Predicate = {
  attribute: 'answers.value',
  comparison: EPredicateOperators.IN,
  value: [],
};

const DEFAULT_DATETIME_PREDICATE: Predicate = {
  attribute: 'answers.value',
  comparison: EPredicateOperators.BETWEEN,
  value: [null, null],
};

const DEFAULT_NUMBER_PREDICATE: Predicate = {
  attribute: 'answers.value',
  // @ts-expect-error
  comparison: null,
  value: 0
};

type QuestionSelectorProps = {
  predicate: { id: string, answer: AnswerPredicate | null };
  options: ScreenQuestionItem[];
  onChange: (newValue: unknown) => void;
};

const QuestionSelector = memo(({
  predicate,
  options = [],
  onChange,
}: QuestionSelectorProps) => {
  const { t } = useTranslation();

  const selectedQuestion = options.find((option) => option.id === predicate.id);

  const renderSelector = () => {
    const { answer } = predicate;
    const onAnswerChange = (newAnswer: any) => onChange({
      ...predicate,
      answer: {
        attribute: 'answers.value',
        ...predicate.answer,
        ...newAnswer,
      },
    });

    // Some of the selectors need a default value, because the predicate
    // won't be set until the selector changes the value
    switch (selectedQuestion?.type) {
      case 'question_boolean':
        const onCheck = (value: boolean) => onAnswerChange({
          comparison: EPredicateOperators.EQ,
          value,
        });

        return (
          <>
            <Dropdown.CheckboxItem
              label={selectedQuestion.parameters.label_true}
              value={answer?.value === true}
              onChange={() => onCheck(true)}
            />
            <Dropdown.CheckboxItem
              label={selectedQuestion.parameters.label_false}
              value={answer?.value === false}
              onChange={() => onCheck(false)}
            />
          </>
        );

      case 'question_datetime':
        return (
          <PredicateSelectorDate
            predicate={answer || DEFAULT_DATETIME_PREDICATE}
            onChange={onAnswerChange}
          />
        );

      case 'question_text':
        const handleSearch = (searchValue: string | null) => {
          onAnswerChange({
            comparison: EPredicateOperators.CONTAINS,
            value: searchValue || '',
          });
        };

        return (
          <div className="dropdown-input">
            <SearchBar
              debounce
              filter={false}
              onSearch={handleSearch}
              defaultValue={answer?.value as string || ''}
              placeholder={t('forms:search_submissions')}
            />
          </div>
        );

      case 'question_checklist':
      case 'question_dropdown':
      case 'question_multiple_choice':
        return (
          <PredicateSelectorMultiSelect
            labelKey="value"
            valueKey="id"
            predicate={answer || DEFAULT_PREDICATE}
            // @ts-expect-error
            options={selectedQuestion.parameters.options}
            onChange={onAnswerChange}
          />
        );

      case 'question_number':
      case 'question_slider':
        return (
          <PredicateSelectorNumber
            predicate={answer || DEFAULT_NUMBER_PREDICATE}
            onChange={onAnswerChange}
          />
        );

      default:
        return null;
    }
  };

  const handleQuestionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newId = e.target.value;
    if (!newId) {
      onChange({ id: null, answer: null });
    } else {
      onChange({ id: newId, answer: null });
    }
  };

  return (
    <>
      <div className="dropdown-input">
        <select
          value={predicate.id}
          onChange={handleQuestionChange}
        >
          <option value="">{`-- ${t('common:predicates_filter_select')} --`}</option>
          {options.map(({ id, parameters }) => (
            <option key={id} value={id}>{parameters.text}</option>
          ))}
        </select>
      </div>
      {renderSelector()}
    </>
  );
});

export default QuestionSelector;
